// frontend/src/utils/librarySearch.js

export const normaliseSearchText = (value) => {
  return String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
};

export const parseLibrarySearch = (query) => {
  const rawQuery = String(query ?? '').trim();

  if (!rawQuery) {
    return {
      positiveClauses: [],
      negativeClauses: [],
      hasSearch: false,
    };
  }

  const rawTokens = rawQuery.match(/-?"[^"]+"|-?[^,\s]+/g) || [];

  const positiveClauses = [];
  const negativeClauses = [];

  rawTokens.forEach((rawToken) => {
    let token = rawToken.trim();

    if (!token) return;

    const isNegative = token.startsWith('-');

    if (isNegative) {
      token = token.slice(1).trim();
    }

    if (!token) return;

    const isQuoted =
      token.length >= 2 &&
      token.startsWith('"') &&
      token.endsWith('"');

    if (isQuoted) {
      token = token.slice(1, -1);
    }

    const alternatives = token
      .split('|')
      .map((alternative) => normaliseSearchText(alternative))
      .filter(Boolean)
      .map((value) => ({
        value,
        exactPhrase: isQuoted,
      }));

    if (alternatives.length === 0) return;

    const clause = {
      alternatives,
      isNegative,
    };

    if (isNegative) {
      negativeClauses.push(clause);
    } else {
      positiveClauses.push(clause);
    }
  });

  return {
    positiveClauses,
    negativeClauses,
    hasSearch:
      positiveClauses.length > 0 ||
      negativeClauses.length > 0,
  };
};

const getWordList = (text) => {
  return normaliseSearchText(text)
    .split(/[^a-z0-9]+/g)
    .filter(Boolean);
};

const scoreAlternativeAgainstField = (
  fieldValue,
  alternative,
  fieldWeight
) => {
  const text = normaliseSearchText(fieldValue);
  const term = normaliseSearchText(alternative.value);

  if (!text || !term) return 0;

  if (alternative.exactPhrase) {
    if (text === term) {
      return 130 * fieldWeight;
    }

    if (text.includes(term)) {
      return 105 * fieldWeight;
    }

    return 0;
  }

  const words = getWordList(text);

  if (text === term) {
    return 120 * fieldWeight;
  }

  if (words.includes(term)) {
    return 100 * fieldWeight;
  }

  if (words.some((word) => word.startsWith(term))) {
    return 80 * fieldWeight;
  }

  if (text.startsWith(term)) {
    return 70 * fieldWeight;
  }

  if (text.includes(term)) {
    return 50 * fieldWeight;
  }

  return 0;
};

const getClauseScore = (clause, searchableFields) => {
  let bestScore = 0;

  clause.alternatives.forEach((alternative) => {
    searchableFields.forEach((field) => {
      const score = scoreAlternativeAgainstField(
        field.value,
        alternative,
        field.weight
      );

      bestScore = Math.max(bestScore, score);
    });
  });

  return bestScore;
};

export const getLibrarySearchScore = (
  query,
  searchableFields
) => {
  const parsed = parseLibrarySearch(query);

  if (!parsed.hasSearch) {
    return 0;
  }

  const safeFields = searchableFields
    .filter(
      (field) =>
        field?.value !== undefined &&
        field?.value !== null
    )
    .map((field) => ({
      value: field.value,
      weight:
        typeof field.weight === 'number'
          ? field.weight
          : 1,
    }));

  for (const clause of parsed.negativeClauses) {
    if (getClauseScore(clause, safeFields) > 0) {
      return -1;
    }
  }

  let totalScore = 0;

  for (const clause of parsed.positiveClauses) {
    const clauseScore = getClauseScore(
      clause,
      safeFields
    );

    if (clauseScore === 0) {
      return -1;
    }

    totalScore += clauseScore;
  }

  return totalScore;
};